// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export type SettingsSectionKey =
  | 'agent'
  | 'sign-ins'
  | 'policies'
  | 'org'
  | 'identity'
  | 'scm'
  | 'notifications'
  | 'app-lifecycle'
  | 'billing'
  | 'maintenance'
  | 'danger';

export interface SettingsNavItem {
  key: SettingsSectionKey;
  label: string;
  /** A lucide component, e.g. Terminal, Building2, KeyRound, GitBranch, Bell, Wrench, AlertTriangle */
  icon: LucideIcon;
  /** Optional mono pill text (e.g. a count badge). */
  badge?: string;
}

interface SettingsNavProps {
  items: ReadonlyArray<SettingsNavItem>;
  active: SettingsSectionKey;
  onSelect: (key: SettingsSectionKey) => void;
  className?: string;
}

/** Mobile tab strip layout modes derived from available width. */
export type MobileStripLayout = 'full' | 'icon' | 'icon-scroll';

/** Require this much spare width beyond the label strip before showing text (avoids cramped tabs). */
const MOBILE_FULL_COMFORT_PX = 48;
/** First-paint fallback per-tab width before the off-screen measurer runs (typical label length). */
const MOBILE_LABELED_TAB_PX = 96;
/** First-paint fallback for icon-only tabs (min touch target + padding). */
const MOBILE_ICON_TAB_PX = 44;
const MOBILE_TAB_GAP_PX = 4;
const MOBILE_STRIP_PADDING_PX = 32;

export function estimateFullStripWidth(itemCount: number): number {
  if (itemCount <= 0) return 0;
  return (
    MOBILE_STRIP_PADDING_PX +
    itemCount * MOBILE_LABELED_TAB_PX +
    (itemCount - 1) * MOBILE_TAB_GAP_PX
  );
}

export function estimateIconStripWidth(itemCount: number): number {
  if (itemCount <= 0) return 0;
  return (
    MOBILE_STRIP_PADDING_PX + itemCount * MOBILE_ICON_TAB_PX + (itemCount - 1) * MOBILE_TAB_GAP_PX
  );
}

export function resolveMobileStripLayout(
  available: number,
  measuredFullWidth: number,
  measuredIconWidth: number,
  itemCount: number,
): MobileStripLayout {
  if (available <= 0) return itemCount > 3 ? 'icon' : 'full';

  // Prefer off-screen scrollWidth (actual label strings). Estimates are fallback
  // only for the first paint before layout; long future labels are measured automatically.
  const fullWidth = measuredFullWidth > 0 ? measuredFullWidth : estimateFullStripWidth(itemCount);
  const iconWidth = measuredIconWidth > 0 ? measuredIconWidth : estimateIconStripWidth(itemCount);

  if (fullWidth + MOBILE_FULL_COMFORT_PX <= available) return 'full';
  if (iconWidth <= available) return 'icon';
  return 'icon-scroll';
}

function initialMobileLayout(itemCount: number): MobileStripLayout {
  return itemCount > 3 ? 'icon' : 'full';
}

function useMobileStripLayout(
  containerRef: React.RefObject<HTMLDivElement | null>,
  visibleRowRef: React.RefObject<HTMLDivElement | null>,
  fullMeasureRef: React.RefObject<HTMLDivElement | null>,
  iconMeasureRef: React.RefObject<HTMLDivElement | null>,
  itemCount: number,
): MobileStripLayout {
  const [layout, setLayout] = React.useState<MobileStripLayout>(() =>
    initialMobileLayout(itemCount),
  );

  React.useLayoutEffect(() => {
    const recalc = (): void => {
      const container = containerRef.current;
      const visible = visibleRowRef.current;
      const full = fullMeasureRef.current;
      const icon = iconMeasureRef.current;
      if (!container || !full || !icon) return;

      const available = container.clientWidth;
      let next = resolveMobileStripLayout(available, full.scrollWidth, icon.scrollWidth, itemCount);

      // If we still chose labels but the live strip overflows, collapse immediately.
      if (next === 'full' && visible && visible.scrollWidth > visible.clientWidth + 1) {
        next = icon.scrollWidth <= available ? 'icon' : 'icon-scroll';
      }

      setLayout(next);
    };

    recalc();
    if (typeof ResizeObserver === 'undefined') return;

    const container = containerRef.current;
    const visible = visibleRowRef.current;
    const full = fullMeasureRef.current;
    const icon = iconMeasureRef.current;
    if (!container || !full || !icon) return;

    const observer = new ResizeObserver(recalc);
    observer.observe(container);
    observer.observe(full);
    observer.observe(icon);
    if (visible) observer.observe(visible);
    return () => observer.disconnect();
  }, [containerRef, visibleRowRef, fullMeasureRef, iconMeasureRef, itemCount]);

  return layout;
}

interface MobileTabButtonProps {
  item: SettingsNavItem;
  isActive: boolean;
  layout: MobileStripLayout;
  onSelect: (key: SettingsSectionKey) => void;
  buttonRef?: (el: HTMLButtonElement | null) => void;
}

function mobileTabClassName(
  isActive: boolean,
  isDanger: boolean,
  layout: MobileStripLayout,
): string {
  return cn(
    'inline-flex items-center h-11 text-[13px] font-medium border-b-2 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-inset',
    layout === 'full' && 'gap-1.5 px-3 whitespace-nowrap shrink-0',
    layout === 'icon' && 'flex-1 justify-center gap-0 px-1 min-w-0',
    layout === 'icon-scroll' && 'shrink-0 justify-center gap-0 min-w-11 px-2',
    isActive
      ? isDanger
        ? 'border-gl-red-text text-gl-red-text'
        : 'border-primary text-foreground'
      : 'border-transparent text-muted-foreground hover:text-foreground',
    isDanger && !isActive && 'text-gl-red-text',
  );
}

function MobileTabButton({
  item,
  isActive,
  layout,
  onSelect,
  buttonRef,
}: MobileTabButtonProps): React.JSX.Element {
  const isDanger = item.key === 'danger';
  const iconOnly = layout !== 'full';

  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={() => onSelect(item.key)}
      aria-current={isActive ? 'page' : undefined}
      aria-label={iconOnly ? item.label : undefined}
      title={iconOnly ? item.label : undefined}
      className={mobileTabClassName(isActive, isDanger, layout)}
    >
      <item.icon className="size-4 shrink-0" aria-hidden="true" />
      {layout === 'full' ? <span>{item.label}</span> : null}
    </button>
  );
}

/**
 * Section navigator for the /settings page.
 * lg+: sticky vertical rail. <lg: tab strip that collapses to icons when labels won't fit comfortably.
 * Presentational — no data fetching, no RBAC gating.
 */
export function SettingsNav({
  items,
  active,
  onSelect,
  className,
}: SettingsNavProps): React.JSX.Element {
  const tabRefs = React.useRef<Map<SettingsSectionKey, HTMLButtonElement | null>>(new Map());
  const mobileStripRef = React.useRef<HTMLDivElement | null>(null);
  const visibleRowRef = React.useRef<HTMLDivElement | null>(null);
  const fullMeasureRef = React.useRef<HTMLDivElement | null>(null);
  const iconMeasureRef = React.useRef<HTMLDivElement | null>(null);
  const mobileLayout = useMobileStripLayout(
    mobileStripRef,
    visibleRowRef,
    fullMeasureRef,
    iconMeasureRef,
    items.length,
  );

  React.useEffect(() => {
    if (mobileLayout !== 'icon-scroll') return;
    const el = tabRefs.current.get(active);
    if (!el) return;
    if (typeof el.scrollIntoView !== 'function') return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({
      inline: 'nearest',
      block: 'nearest',
      behavior: prefersReduced ? 'instant' : 'smooth',
    });
  }, [active, mobileLayout]);

  return (
    <nav aria-label="Settings sections" className={cn(className)}>
      {/* ── lg+ vertical rail ── */}
      <div className="hidden lg:flex flex-col gap-0.5 sticky top-6">
        {items.map((item) => {
          const isActive = active === item.key;
          const isDanger = item.key === 'danger';
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onSelect(item.key)}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'flex items-center gap-2.5 h-9 px-2.5 rounded-gl text-[13px] font-medium text-left transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-inset',
                isActive
                  ? 'bg-accent text-foreground font-semibold'
                  : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                isDanger && 'text-gl-red-text',
              )}
            >
              <item.icon
                className={cn('size-4 shrink-0', isActive && !isDanger && 'text-primary')}
                aria-hidden="true"
              />
              <span className="flex-1">{item.label}</span>
              {item.badge !== undefined && (
                <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Off-screen measurers — unconstrained width so scrollWidth is accurate. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed -left-[10000px] top-0 w-max invisible"
      >
        <div ref={fullMeasureRef} className="inline-flex w-max gap-1 px-4">
          {items.map((item) => (
            <MobileTabButton
              key={`measure-full-${item.key}`}
              item={item}
              isActive={false}
              layout="full"
              onSelect={() => undefined}
            />
          ))}
        </div>
        <div ref={iconMeasureRef} className="inline-flex w-max gap-1 px-4">
          {items.map((item) => (
            <MobileTabButton
              key={`measure-icon-${item.key}`}
              item={item}
              isActive={false}
              layout="icon-scroll"
              onSelect={() => undefined}
            />
          ))}
        </div>
      </div>

      {/* ── <lg adaptive tab strip ── */}
      <div ref={mobileStripRef} className="relative -mx-4 lg:hidden">
        <div
          ref={visibleRowRef}
          data-mobile-layout={mobileLayout}
          className={cn(
            'flex gap-1 px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            mobileLayout === 'full' && 'overflow-x-hidden',
            mobileLayout === 'icon' && 'w-full overflow-x-hidden',
            mobileLayout === 'icon-scroll' && 'overflow-x-auto',
          )}
        >
          {items.map((item) => (
            <MobileTabButton
              key={item.key}
              item={item}
              isActive={active === item.key}
              layout={mobileLayout}
              onSelect={onSelect}
              buttonRef={(el) => {
                tabRefs.current.set(item.key, el);
              }}
            />
          ))}
        </div>

        {mobileLayout === 'icon-scroll' ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent"
          />
        ) : null}
      </div>
    </nav>
  );
}
